import React from "react";
import { observer, Observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FormSpy } from "react-final-form";
import { Button, Typography } from "@material-ui/core";
import ContentStudioWorkoutsStore from "stores/ContentStudioWorkoutsStore";
import WorkoutTypeDictionary from "stores/dictionary/WorkoutTypeDictionary";
import Search from "components/Input/Search";
import FormControl, { TextFieldControl } from "components/FormControl";
import LoadingButton from "components/LoadingButton";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import CardListFieldControl from "components/FormControl/CardListFieldControl";
import Loader from "components/Layout/Loader";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import { SNACKBAR_TYPES } from "components/Snackbar";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import { TableSortType } from "stores/templates/TableStore";
import { FILTER_COLUMNS } from "constants/filterConstants";
import {
  Container,
  Row,
  CardListRow,
  Buttons,
  ButtonContainer,
  SearchControlWrapper,
  SearchControls,
  TitleContainer,
} from "./styles";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
} & SnackbarContextProps;

const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class WorkoutForm extends React.Component<Props> {
  @observable initialValues: any = {};
  @observable updateConfirmationIsOpened: boolean = false;

  @computed get customOption() {
    return WorkoutTypeDictionary.options.find(
      ({ label }) => label === "Custom",
    );
  }

  @computed get hasMoreInfo() {
    return ContentStudioWorkoutsStore.hasMoreInfo;
  }

  @computed get searchValue() {
    return ContentStudioWorkoutsStore.search;
  }

  @computed get options() {
    return ContentStudioWorkoutsStore.workoutsOptions;
  }

  componentDidMount() {
    ContentStudioWorkoutsStore.fetch();
  }

  componentWillUnmount() {
    ContentStudioWorkoutsStore.reset();
  }

  fetchMoreWorkouts = async () => {
    const { setMessage } = this.props;

    try {
      await ContentStudioWorkoutsStore.fetchMoreWorkouts();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ContentStudioWorkoutsStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      ContentStudioWorkoutsStore.fetch();
  };

  onSearchClear = () => {
    ContentStudioWorkoutsStore.clearSearch();
    ContentStudioWorkoutsStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentStudioWorkoutsStore.applyOrder(value);
    ContentStudioWorkoutsStore.resetPage();
    ContentStudioWorkoutsStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      ContentStudioWorkoutsStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    ContentStudioWorkoutsStore.resetPage();
    ContentStudioWorkoutsStore.fetch();
  };

  onFilterClear = () => {
    ContentStudioWorkoutsStore.clearFilter(FILTER_COLUMNS.ACTIVE_PROGRAMS);
    ContentStudioWorkoutsStore.clearFilter(FILTER_COLUMNS.PUBLISH_STATUS);
    ContentStudioWorkoutsStore.fetch();
  };

  onChangeIsDirty = ({ dirty, values }: { dirty: boolean; values: any }) => {
    const { onChangeIsDirty } = this.props;

    if (Object.keys(values).length === 1) {
      onChangeIsDirty({ dirty: false });
      return;
    }

    onChangeIsDirty({ dirty });
  };

  onChangeWorkoutTypeCallback = (newValue: string, previousValue: string) => {
    if (previousValue !== newValue && this.searchValue) {
      ContentStudioWorkoutsStore.resetPage();
      this.onSearchClear();
    }
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    const { onSubmit } = this.props;

    this.closeUpdateConfirmation();
    onSubmit(values);
  };

  validateForm = (values: any): boolean => {
    let isValid = true;

    if (this.customOption) {
      isValid = this.customOption.label in values ? !!values.customLabel : true;
    }

    return isValid;
  };

  onSubmit = (values: any) => {
    const { onSubmit, setMessage } = this.props;

    const formIsValid = this.validateForm(values);

    if (formIsValid) {
      const transformedValues = {
        ...values,
        workoutId: values.workoutId?.id || values.workoutId,
      };

      onSubmit(transformedValues);
      return;
    }

    setMessage("Please specify a label.", SNACKBAR_TYPES.ERROR);
    this.initialValues = {
      ...values,
      workoutLabel: this.customOption?.value,
      updateDateTime: new Date(),
    };
  };

  render() {
    const { onClose } = this.props;

    return (
      <FormControl onSubmit={this.onSubmit} initialValues={this.initialValues}>
        <FormSpy
          subscription={{ dirty: true, values: true }}
          onChange={this.onChangeIsDirty}
        />
        <Container>
          <Row>
            <TitleContainer>
              <Typography variant="h1" color="textPrimary">
                Add Sequence Day
              </Typography>
            </TitleContainer>
          </Row>

          <Row>
            <TextFieldControl
              variant="outlined"
              name="day"
              label="Day Number"
              placeholder="Day Number"
              type="number"
              numericLimit={3}
              style={{ width: "150px" }}
              required
            />
          </Row>
          <Row>
            <Search
              initValue={ContentStudioWorkoutsStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />

            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={
                    ContentStudioWorkoutsStore.orderContext.appliedOrderInfo
                  }
                  onApply={this.onSortApply}
                  disablePortal
                />
              </SearchControlWrapper>
              <SearchControlWrapper>
                <FilteringControl
                  initValue={ContentStudioWorkoutsStore.filters}
                  onApply={this.onFilterApply}
                  onClear={this.onFilterClear}
                  statusFiltering
                />
              </SearchControlWrapper>
            </SearchControls>
          </Row>

          <CardListRow id="scrollableDiv">
            {!ContentStudioWorkoutsStore.isLoading ? (
              <FormSpy subscription={{ values: true }}>
                {({ values }) => {
                  const [fieldName] = WorkoutTypeDictionary.options.filter(
                    ({ value }) => value === values.workoutLabel,
                  );

                  return (
                    <Observer>
                      {() => (
                        <CardListFieldControl
                          name="workoutId"
                          options={this.options}
                          multi={true}
                          hasMore={this.hasMoreInfo}
                          fetchMore={this.fetchMoreWorkouts}
                          searchValue={this.searchValue}
                          noItemsPlaceholderText={"No workouts added yet."}
                          scrollableTarget="scrollableDiv"
                          withInfinityScroll
                        />
                      )}
                    </Observer>
                  );
                }}
              </FormSpy>
            ) : (
              <Loader />
            )}
          </CardListRow>
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => (
        <LoadingButton
          onClick={onClick}
          variant="contained"
          color="primary"
          isLoading={this.props.isLoading}
          fullWidth
          disabled={!values.workoutId}
        >
          Add
        </LoadingButton>
      )}
    </FormSpy>
  );
}

export default withGlobalSnackbar(WorkoutForm);
